/**
 * Chat Pharu (ADR-0077) — a página "Chat" do ERP, o mesmo chat do PharuCut.
 *
 * Três colunas (lista, conversa, arquivos e pessoas); no celular uma por
 * vez. Tempo real por SSE v2 (`services/chat.abrirStream`): o aviso traz o que
 * mudou já pronto e a tela aplica sem reler; conversa nova ou quem entra e sai
 * ainda chega como "mudou", e aí a tela relê pela API. A mensagem enviada
 * aparece na hora (pendente) e é trocada pela confirmada quando o servidor responde.
 * Rotas: /chat, /chat/:conversaId e /chat?lote=ID (conversa do lote).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { avisarNaoLidasDoChat } from "../../services/notificacoesDoChat";
import { atualizarOperador } from "../../services/producao";
import {
  abrirStream,
  adicionarParticipante,
  agendarMensagem,
  agruparConversas,
  agruparPorDia,
  alterarConversa,
  apagarConversaParaMim,
  apagarMensagem,
  aplicarPacoteNaLista,
  aplicarPacoteNasMensagens,
  avisarDigitando,
  cancelarAgendada,
  comprimirFoto,
  copiarTexto,
  definirRenovadorDeSessao,
  ehPendente,
  encaminharMensagem,
  enviarComProgresso,
  fixarMensagem,
  juntarMensagens,
  listarAgendadas,
  mensagemPendente,
  limparAnexos,
  configuracaoDoChat,
  contatosDaBusca,
  conversaDoLote,
  criarDireta,
  criarGrupo,
  editarMensagem,
  horaCurta,
  listarConversas,
  listarMensagens,
  marcarAudioOuvido,
  marcarLido,
  marcarOuvidoNaMensagem,
  mesmaPessoaDaDireta,
  notificarDeNovo,
  novoIdDoCliente,
  obterConversa,
  pessoas,
  quandoCurto,
  reagir,
  removerParticipante,
  temConversaForaDaLista,
  mensagensQueTocam,
  textoDoLembrete,
  buscar,
  marcarPresenca,
  preferenciasDoChat,
  salvarPreferenciasDoChat,
  tituloComContagem,
  totalNaoLidas,
  virarTarefa,
} from "../../services/chat";
import { AgendarModal, EncaminharModal, TarefaModal } from "./AcoesDaMensagem";
import { Assistente } from "./Assistente";
import { aplicarTomDePele } from "./Emojis";
import { Balao } from "./Balao";
import { Digitando } from "./Marcas";
import { PreviaDaUltima } from "./PreviaDaUltima";
import { ConfiguracoesDoChat } from "./ConfiguracoesDoChat";
import { EscolhaDePessoas, Modal } from "./Dialogos";
import { Escrita } from "./Escrita";
import { Avatar, ICONE, Ic } from "./Icones";
import { Visualizador, tipoDoArquivo } from "./Midia";
import { PainelDaConversa } from "./PainelDaConversa";
import { PERTO_DO_FIM_PX, useGrudarNoFim } from "./grudarNoFim";
import { abrirChatNaLateral, enderecoDaTelaCheia, fecharChatDaLateral } from "./lateral";
import { useNavegacaoDoChat } from "./navegacao";
import { escolherSom, tocarAviso } from "./som";


// Carregar anteriores até achar a mensagem pedida (busca, link da tarefa): até 10 páginas.
const PAGINAS_ATE_ACHAR = 10;
const DESTAQUE_MS = 2500;
const TITULO_DO_CHAT = "Chat · PharuERP";

/** As mensagens ainda subindo de uma conversa (o mapa é id_do_cliente → {mensagem, envio}). */
const pendentesDa = (pendentes, conversaId) =>
  [...pendentes.values()].filter((p) => p.mensagem.conversa_id === conversaId).map((p) => p.mensagem);

/** A largura da tela acompanha girar o tablet e redimensionar (antes era lida uma vez só). */
function useLarguraMinima(consulta) {
  const [casa, setCasa] = useState(() => typeof window !== "undefined" && window.matchMedia(consulta).matches);
  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const aoMudar = () => setCasa(mq.matches);
    mq.addEventListener?.("change", aoMudar);
    return () => mq.removeEventListener?.("change", aoMudar);
  }, [consulta]);
  return casa;
}

// ---------- a página ----------

/**
 * O chat é de UMA pessoa. Trocou o usuário na mesma aba (outra sessão gravada
 * por cima, sem recarregar), a tela inteira renasce vazia: nada do que estava
 * em memória — conversas, mensagens, arquivos, stream — atravessa a troca.
 */
/**
 * `naLateral`: o mesmo chat na coluna da direita (`ChatNaLateral`) — uma
 * coluna por vez, como no celular, e trocar de conversa não muda o endereço.
 */
export default function ChatPage({ naLateral = false }) {
  const { usuario } = useAuth();
  const quem = usuario?.id || "sem-usuario";
  useEffect(() => limparAnexos, [quem]);
  return <ChatDaPessoa key={quem} naLateral={naLateral} />;
}

function ChatDaPessoa({ naLateral }) {
  const { request, hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { conversaId, mensagemPedida, loteId, irPara, irParaLista, esquecerParametros } = useNavegacaoDoChat(naLateral);
  const podeEnviar = hasPermission("chat:enviar");
  const podeGrupos = hasPermission("chat_grupos:gerenciar");

  const [conversas, setConversas] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [busca, setBusca] = useState("");
  const [detalhe, setDetalhe] = useState(null);
  const [erroDaConversa, setErroDaConversa] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [temMais, setTemMais] = useState(false);
  const [digitando, setDigitando] = useState({});
  const [estadoDoStream, setEstadoDoStream] = useState("reconectando");
  const [config, setConfig] = useState(null);
  const [respostaA, setRespostaA] = useState(null);
  const [editando, setEditando] = useState(null);
  const [apagando, setApagando] = useState(null);
  const [visualizando, setVisualizando] = useState(null);
  const [nova, setNova] = useState(false);
  const [modoNova, setModoNova] = useState("direta");
  const [nomeDoGrupo, setNomeDoGrupo] = useState("");
  const [adicionando, setAdicionando] = useState(false);
  const [renomeando, setRenomeando] = useState(null);
  const [painelAberto, setPainelAberto] = useState(false);
  const telaLarga = useLarguraMinima("(min-width: 1101px)");
  // Fase B e C: os modais das ações e o que está agendado nesta conversa.
  const [encaminhando, setEncaminhando] = useState(null);
  const [tarefaDe, setTarefaDe] = useState(null);
  const [agendando, setAgendando] = useState(null);
  const [agendadas, setAgendadas] = useState([]);
  const [configAberta, setConfigAberta] = useState(false);
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const [apagandoConversa, setApagandoConversa] = useState(false);
  const [destacada, setDestacada] = useState(null);
  // Fase 2: cartões vivos releem quando o stream avisa; busca no servidor com 3+ letras.
  const [versaoDosCartoes, setVersaoDosCartoes] = useState(0);
  const [achados, setAchados] = useState(null);
  // A busca também acha contatos sem conversa: a lista de pessoas vem uma vez, na primeira letra.
  const [pessoasDaUnidade, setPessoasDaUnidade] = useState(null);
  const querContatos = podeEnviar && busca.trim().length > 0;
  useEffect(() => {
    if (!querContatos || pessoasDaUnidade) return;
    pessoas(request).then(setPessoasDaUnidade).catch(() => setPessoasDaUnidade({ usuarios: [], operadores: [] }));
  }, [querContatos, pessoasDaUnidade, request]);
  const contatos = useMemo(
    () => (querContatos ? contatosDaBusca(pessoasDaUnidade, busca, conversas) : []),
    [querContatos, pessoasDaUnidade, busca, conversas],
  );
  const abrirDiretaCom = (p) =>
    criarDireta(request, { usuarioId: p.tipo === "usuario" ? p.id : undefined, operadorId: p.tipo === "operador" ? p.id : undefined })
      .then((d) => { setBusca(""); recarregarLista(); irPara(d.id); })
      .catch(() => undefined);
  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 3) { setAchados(null); return undefined; }
    const id = setTimeout(() => buscar(request, termo).then((r) => setAchados(r?.resultados || [])).catch(() => setAchados([])), 300);
    return () => clearTimeout(id);
  }, [busca, request]);
  const [abaVisivel, setAbaVisivel] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const aoMudar = () => setAbaVisivel(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", aoMudar);
    return () => document.removeEventListener("visibilitychange", aoMudar);
  }, []);
  // Presença: liga quando a aba fica visível (antes, aberta escondida, nunca ligava).
  useEffect(() => {
    if (!abaVisivel) return undefined;
    marcarPresenca(request).catch(() => undefined);
    const id = setInterval(() => marcarPresenca(request).catch(() => undefined), 30000);
    return () => clearInterval(id);
  }, [request, abaVisivel]);
  // A foto do balão com a sessão vencida renova o token e tenta de novo.
  useEffect(() => {
    definirRenovadorDeSessao(() => request("/api/me", { silentError: true }));
    return () => definirRenovadorDeSessao(null);
  }, [request]);
  const fluxoRef = useRef(null);
  // Abre na mais recente e fica lá enquanto fotos e cartões terminam de carregar.
  const grudadoRef = useGrudarNoFim(fluxoRef, ".pchat-msg, .pchat-dia", conversaId, mensagens.length);
  const ultimaLidaRef = useRef(null);
  const telaEstreita = useLarguraMinima("(max-width: 767px)");
  const estreita = telaEstreita || naLateral;

  const recarregarLista = useCallback(() => {
    listarConversas(request).then((lista) => { setConversas(lista || []); setCarregandoLista(false); }).catch(() => setCarregandoLista(false));
  }, [request]);

  // A conversa que está na tela AGORA: resposta de outra (que demorou) é descartada.
  const conversaAtualRef = useRef(conversaId);
  conversaAtualRef.current = conversaId;
  // A lista de agora, para o stream saber o que está silenciado sem reabrir.
  const conversasRef = useRef(conversas);
  // As mensagens que já tocaram: uma reação reenvia a mensagem pelo stream.
  const avisadasRef = useRef(new Set());
  conversasRef.current = conversas;
  // Com o stream v2 de pé, o que muda chega pronto nele: a tela não relê depois de agir.
  const conectadoRef = useRef(false);
  // Mensagens que a pessoa mandou e o servidor ainda não confirmou (id_do_cliente →
  // a mensagem na tela + o que enviar). Sobrevivem à releitura e à troca de conversa.
  const pendentesRef = useRef(new Map());
  const filaDeEnvioRef = useRef(Promise.resolve());
  const urlsLocaisRef = useRef([]);
  useEffect(() => () => urlsLocaisRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const recarregarConversa = useCallback((id) => {
    if (!id || conversaAtualRef.current !== id) return;
    obterConversa(request, id).then((d) => {
      if (conversaAtualRef.current !== id) return;
      setDetalhe(d); setErroDaConversa(false);
    }).catch(() => {
      if (conversaAtualRef.current !== id) return;
      // Saí (ou fui tirado) da conversa: o que estava na tela some com ela.
      setErroDaConversa(true); setDetalhe(null); setMensagens([]); setTemMais(false);
    });
    listarMensagens(request, id).then((pagina) => {
      if (conversaAtualRef.current !== id) return;
      setMensagens((atuais) => {
        const novas = pagina?.mensagens || [];
        const ids = new Set(novas.map((m) => m.id));
        // Mantém as anteriores já carregadas; a página nova substitui o fim; a
        // que ainda está subindo continua no fim (antes a releitura a apagava).
        const primeiraNova = novas[0]?.criada_em || "";
        const antigas = atuais.filter((m) => !ehPendente(m) && !ids.has(m.id) && m.criada_em < primeiraNova);
        return juntarMensagens([...antigas, ...pendentesDa(pendentesRef.current, id)], novas);
      });
      if (pagina) setTemMais((v) => v || !!pagina.tem_mais);
    }).catch(() => undefined);
  }, [request]);

  useEffect(() => {
    recarregarLista();
    configuracaoDoChat(request).then(setConfig).catch(() => undefined);
    // O tom de pele é da pessoa: aplica antes de qualquer seletor abrir.
    preferenciasDoChat(request).then((p) => {
      if (!p) return;
      aplicarTomDePele(p.tom_de_pele);
      escolherSom(p.som_do_aviso);
    }).catch(() => undefined);
  }, [recarregarLista, request]);

  useEffect(() => {
    setDetalhe(null); setMensagens([]); setTemMais(false); setRespostaA(null); setEditando(null); setErroDaConversa(false); setPainelAberto(false);
    ultimaLidaRef.current = null;
    recarregarConversa(conversaId);
  }, [conversaId, recarregarConversa]);

  // `/chat?lote=ID`: cria/entra na conversa do lote e troca para a rota dela.

  useEffect(() => {
    if (!loteId) return;
    conversaDoLote(request, loteId).then((d) => { esquecerParametros(); irPara(d.id, { substituir: true }); }).catch(() => toast.error("Não deu para abrir a conversa do lote."));
  }, [loteId, request, irPara, esquecerParametros, toast]);

  // Tempo real: o servidor diz o que mudou; a tela relê SÓ isso.
  useEffect(() => {
    const parar = abrirStream({
      aoEstado: (estado) => {
        conectadoRef.current = estado === "conectado";
        setEstadoDoStream(estado);
        if (estado !== "conectado") setDigitando({});
      },
      aoDigitando: setDigitando,
      // v2: o que mudou chega pronto — aplica na lista e na conversa aberta, sem reler.
      aoPacote: (pacote) => {
        setDigitando(pacote.digitando || {});
        setConversas((atuais) => aplicarPacoteNaLista(atuais, pacote));
        // Mensagem nova numa conversa que apaguei para mim: ela volta à lista.
        if (temConversaForaDaLista(conversasRef.current, pacote)) recarregarLista();
        // Som para mensagem nova (regra única em `mensagensQueTocam`, igual no Cut).
        const novas = mensagensQueTocam(pacote, {
          conversaAberta: conversaAtualRef.current,
          abaOculta: document.hidden,
          silenciadas: new Set(conversasRef.current.filter((c) => c.silenciada).map((c) => c.id)),
        }).filter((id) => !avisadasRef.current.has(id));
        novas.forEach((id) => avisadasRef.current.add(id));
        if (novas.length) tocarAviso();
        const atual = conversaAtualRef.current;
        const item = (pacote.conversas || []).find((i) => i.conversa_id === atual);
        if (!item) return;
        setMensagens((atuais) => aplicarPacoteNasMensagens(atuais.filter((m) => m.conversa_id === atual), item));
        if (item.mensagens.length > 0) setVersaoDosCartoes((v) => v + 1);
      },
      // Sessão vencida: um pedido pelo caminho normal renova o token.
      aoSemAcesso: () => listarConversas(request).catch(() => undefined),
      aoAviso: (aviso) => {
        setDigitando(aviso.digitando || {});
        if (aviso.mudaram.length === 0) {
          // Nada a reler: as não lidas chegam prontas no aviso.
          setConversas((atuais) => atuais.map((c) => ({ ...c, nao_lidas: (aviso.nao_lidas || {})[c.id] ?? 0 })));
          return;
        }
        setVersaoDosCartoes((v) => v + 1);
        recarregarLista();
        const atual = conversaAtualRef.current;
        if (atual && aviso.mudaram.includes(atual)) recarregarConversa(atual);
      },
    });
    return parar;
  }, [recarregarLista, recarregarConversa, request]);
  // Sem stream: relê a lista E a conversa aberta (antes só a lista — a mensagem
  // nova só aparecia ao atualizar a tela).
  useEffect(() => {
    if (estadoDoStream === "conectado") return undefined;
    const id = setInterval(() => { recarregarLista(); recarregarConversa(conversaAtualRef.current); }, 15000);
    return () => clearInterval(id);
  }, [estadoDoStream, recarregarLista, recarregarConversa]);

  // Leva o chat para a lateral, na mesma conversa, e volta à tela de antes
  // (ou à Visão Geral, quando o chat foi a primeira tela aberta).
  const raizRef = useRef(null);
  // Tela cheia e fechar: no topo da lista e no cabeçalho da conversa (na
  // lateral só uma das duas aparece por vez).
  const botoesDaLateral = (
    <>
      <button type="button" className="pchat-btn" title="Abrir em tela cheia" aria-label="Abrir o chat em tela cheia" onClick={() => { fecharChatDaLateral(); navigate(enderecoDaTelaCheia(conversaId)); }}><Ic d={ICONE.telaCheia} size={16} /></button>
      <button type="button" className="pchat-btn" title="Fechar o chat" aria-label="Fechar o chat" onClick={fecharChatDaLateral}><Ic d={ICONE.x} size={16} /></button>
    </>
  );
  const levarParaALateral = () => {
    abrirChatNaLateral(conversaId);
    if (window.history.length > 1 && window.history.state?.idx > 0) navigate(-1);
    else navigate("/visao-geral");
  };

  // ESC fecha a conversa e volta ao início do Chat (pedido do Igor, 2026-10-28).
  // Quem já usou o ESC (modal, emoji, visualizador, edição) marca o evento, e a
  // resposta em andamento é cancelada antes — só o ESC "livre" sai da conversa.
  const algumModalAberto = Boolean(apagando || visualizando || nova || adicionando || renomeando || encaminhando
    || tarefaDe || agendando || configAberta || assistenteAberto || saindo || apagandoConversa);
  useEffect(() => {
    if (!conversaId) return undefined;
    const aoTeclar = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented || algumModalAberto) return;
      // Na lateral, o ESC é do chat só com o foco nele: a tela ao lado tem os seus.
      if (naLateral && !raizRef.current?.contains(document.activeElement)) return;
      // Espera os outros ouvintes do mesmo ESC (os modais escutam na janela, depois deste).
      window.setTimeout(() => {
        if (e.defaultPrevented) return;
        if (respostaA) { setRespostaA(null); return; }
        // O botão da conversa clicado na lista ainda tinha o foco: depois de uma
        // tecla o navegador desenhava o contorno branco nele (2026-11-03).
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        irParaLista();
      }, 0);
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [conversaId, algumModalAberto, respostaA, irParaLista, naLateral]);

  // O sino e o menu acertam junto com a lista (antes esperavam até 60 s).
  useEffect(() => {
    if (!carregandoLista) avisarNaoLidasDoChat(conversas);
  }, [conversas, carregandoLista]);

  // Fase B: título da aba e ícone do app com as não lidas (as silenciadas não contam).
  const total = totalNaoLidas(conversas);
  useEffect(() => {
    document.title = tituloComContagem(TITULO_DO_CHAT, total);
    if (navigator.setAppBadge) {
      (total > 0 ? navigator.setAppBadge(total) : navigator.clearAppBadge?.())?.catch?.(() => undefined);
    }
  }, [total]);
  useEffect(() => () => { document.title = "PharuERP"; navigator.clearAppBadge?.()?.catch?.(() => undefined); }, []);

  // As MINHAS mensagens agendadas desta conversa (só eu vejo).
  const recarregarAgendadas = useCallback((id) => {
    if (!id) { setAgendadas([]); return; }
    listarAgendadas(request, id).then((lista) => { if (conversaAtualRef.current === id) setAgendadas(lista || []); }).catch(() => setAgendadas([]));
  }, [request]);
  useEffect(() => { recarregarAgendadas(conversaId); }, [conversaId, recarregarAgendadas]);

  // Busca e link da tarefa: `?mensagem=<id>` leva até ela (carrega anteriores se preciso).
  const paginasProcuradasRef = useRef(0);
  useEffect(() => { paginasProcuradasRef.current = 0; }, [mensagemPedida, conversaId]);

  // Rolagem ao fim e "lida".
  const ultima = mensagens[mensagens.length - 1];
  const ultimaEhMinhaSubindo = ehPendente(ultima);
  useEffect(() => {
    const el = fluxoRef.current;
    if (!el) return;
    const pertoDoFim = el.scrollHeight - el.scrollTop - el.clientHeight < PERTO_DO_FIM_PX;
    // A pessoa acabou de mandar: vê a própria mensagem mesmo se estava lá em cima.
    if (pertoDoFim || ultimaLidaRef.current === null || ultimaEhMinhaSubindo) el.scrollTop = el.scrollHeight;
  }, [ultima?.id, conversaId, ultimaEhMinhaSubindo]);
  useEffect(() => {
    if (!ultima || !conversaId || !abaVisivel || ultimaLidaRef.current === ultima.id) return;
    // Só marca lida a mensagem DESTA conversa (na troca, a lista ainda é da anterior).
    if (ultima.conversa_id !== conversaId) return;
    const alvo = ultima.id;
    ultimaLidaRef.current = alvo;
    if (ultima.minha) return;
    // O contador some na hora; com o stream de pé, o pacote da leitura confirma.
    setConversas((atuais) => atuais.map((c) => (c.id === conversaId ? { ...c, nao_lidas: 0 } : c)));
    marcarLido(request, conversaId, alvo).then(() => { if (!conectadoRef.current) recarregarLista(); }).catch(() => {
      if (ultimaLidaRef.current === alvo) ultimaLidaRef.current = "";
    });
  }, [ultima, conversaId, request, recarregarLista, abaVisivel]);

  const atualizarPendente = useCallback((idDoCliente, mudanca) => {
    const item = pendentesRef.current.get(idDoCliente);
    if (item) item.mensagem = { ...item.mensagem, ...mudanca };
    setMensagens((atuais) => atuais.map((m) => (ehPendente(m) && m.id_do_cliente === idDoCliente ? { ...m, ...mudanca } : m)));
  }, []);

  /** Uma de cada vez, na ordem em que foram escritas (a foto que sobe não passa à frente do texto). */
  const despachar = useCallback((idDoCliente) => {
    filaDeEnvioRef.current = filaDeEnvioRef.current.then(async () => {
      const item = pendentesRef.current.get(idDoCliente);
      if (!item) return;
      const { destino, texto, respostaA, cartao } = item.envio;
      try {
        // Foto de celular (3–8 MB) sobe como JPEG de até 1600 px; a comprimida
        // fica guardada para o "Tentar de novo" não comprimir outra vez.
        // Só o arquivo: o `map` passaria o índice como `maximo` (0 px) e a foto subia sem comprimir.
        const arquivos = await Promise.all((item.envio.arquivos || []).map((a) => comprimirFoto(a)));
        item.envio.arquivos = arquivos;
        let ultimoAviso = 0;
        const aoProgredir = (pct) => {
          if (pct - ultimoAviso < 5 && pct < 100) return; // a cada 5%: não redesenha a conversa por byte
          ultimoAviso = pct;
          atualizarPendente(idDoCliente, { progresso: pct });
        };
        const salva = await enviarComProgresso(request, destino, { texto, idDoCliente, respostaA, arquivos, cartao }, aoProgredir);
        pendentesRef.current.delete(idDoCliente);
        // A foto continua do aparelho: não desce de novo do servidor logo depois de subir.
        const comLocal = { ...salva, anexos: (salva.anexos || []).map((a, i) => ({ ...a, local_url: item.mensagem.anexos[i]?.local_url || null })) };
        if (conversaAtualRef.current === destino) setMensagens((atuais) => juntarMensagens(atuais, [comLocal]));
        if (!conectadoRef.current) { recarregarConversa(destino); recarregarLista(); }
      } catch (erro) {
        atualizarPendente(idDoCliente, { pendente: "falhou" });
        toast.error(erro?.payload?.detalhe || erro?.message || "A mensagem não foi enviada.");
      }
    });
  }, [request, recarregarConversa, recarregarLista, atualizarPendente, toast]);

  /** A mensagem aparece na hora e a caixa fica livre para a próxima; o envio segue na fila. */
  const aoEnviar = async ({ texto, arquivos, respostaA: rid, cartao: cartaoPedido, idDoCliente }) => {
    const destino = conversaId;
    if (editando) {
      const alvo = editando;
      try {
        await editarMensagem(request, alvo.id, texto);
        setEditando(null);
        if (!conectadoRef.current) recarregarConversa(destino);
        return true;
      } catch {
        return false; // o request já avisou; o texto editado fica na caixa
      }
    }
    const id = idDoCliente || novoIdDoCliente();
    const anexos = (arquivos || []).map((f) => {
      const tipo = tipoDoArquivo(f);
      const urlLocal = ["imagem", "video", "audio"].includes(tipo) ? URL.createObjectURL(f) : null;
      if (urlLocal) urlsLocaisRef.current.push(urlLocal);
      return { nome: f.name, tipo, tamanho: f.size, urlLocal };
    });
    const mensagem = mensagemPendente({ idDoCliente: id, conversaId: destino, eu: detalhe?.eu, texto, anexos, respostaA, cartao: cartaoPedido });
    pendentesRef.current.set(id, { mensagem, envio: { destino, texto, arquivos, respostaA: rid, cartao: cartaoPedido } });
    setMensagens((atuais) => [...atuais, mensagem]);
    setRespostaA(null);
    despachar(id);
    return true;
  };

  const carregandoAnterioresRef = useRef(false);
  const carregarAnteriores = async () => {
    const primeira = mensagens[0];
    if (!primeira || carregandoAnterioresRef.current) return;
    carregandoAnterioresRef.current = true;
    const destino = conversaId;
    const el = fluxoRef.current;
    const alturaAntes = el?.scrollHeight || 0;
    try {
      // O marco é a MENSAGEM (id), não o horário.
      const pagina = await listarMensagens(request, destino, primeira.id);
      if (conversaAtualRef.current !== destino) return;
      setMensagens((atuais) => {
        const ja = new Set(atuais.map((m) => m.id));
        return [...(pagina?.mensagens || []).filter((m) => !ja.has(m.id)), ...atuais];
      });
      setTemMais(!!pagina?.tem_mais);
      requestAnimationFrame(() => { if (el) el.scrollTop += el.scrollHeight - alturaAntes; });
    } catch {
      toast.error("Não deu para carregar as mensagens anteriores. Tente de novo.");
    } finally {
      carregandoAnterioresRef.current = false;
    }
  };

  // Vai até a mensagem pedida: rola, destaca e tira o parâmetro; se ainda não
  // carregou, pede as anteriores (até PAGINAS_ATE_ACHAR páginas).
  useEffect(() => {
    if (!mensagemPedida || mensagens.length === 0 || mensagens[0].conversa_id !== conversaId) return;
    const alvo = document.getElementById(`msg-${mensagemPedida}`);
    if (alvo) {
      grudadoRef.current = false; // foi até a mensagem pedida: não volta ao fim sozinho
      alvo.scrollIntoView({ block: "center" });
      setDestacada(mensagemPedida);
      setTimeout(() => setDestacada(null), DESTAQUE_MS);
      esquecerParametros();
      return;
    }
    if (temMais && paginasProcuradasRef.current < PAGINAS_ATE_ACHAR) {
      paginasProcuradasRef.current += 1;
      carregarAnteriores();
    }
    // `carregarAnteriores` muda a cada desenho; quem dispara é a lista de mensagens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensagemPedida, mensagens, temMais, conversaId, esquecerParametros]);

  const aoAgendar = (texto) => new Promise((resolve) => setAgendando({ texto, resolve }));
  const confirmarAgendamento = async (enviarEm) => {
    try {
      await agendarMensagem(request, conversaId, { texto: agendando.texto, enviarEm });
      toast.success(`Agendada para ${new Date(enviarEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.`);
      agendando.resolve(true);
      setAgendando(null);
      recarregarAgendadas(conversaId);
    } catch {
      /* o request já avisou; o modal fica aberto para corrigir a hora */
    }
  };
  const fecharAgendamento = () => { agendando?.resolve(false); setAgendando(null); };

  const confirmarEncaminhar = async (ids) => {
    try {
      await encaminharMensagem(request, encaminhando.id, ids);
      toast.success(ids.length === 1 ? "Mensagem encaminhada." : `Encaminhada para ${ids.length} conversas.`);
      setEncaminhando(null);
      if (!conectadoRef.current) recarregarLista();
    } catch {
      /* o request já avisou */
    }
  };

  const confirmarTarefa = async (dados) => {
    try {
      const r = await virarTarefa(request, tarefaDe.id, dados);
      toast.success(`Tarefa "${r.titulo}" criada na Agenda.`);
      setTarefaDe(null);
    } catch {
      /* o request já avisou */
    }
  };

  const sairDoGrupo = async () => {
    setSaindo(false);
    try {
      await removerParticipante(request, detalhe.id, detalhe.eu.id);
      recarregarLista();
      irParaLista();
    } catch {
      /* o request já avisou */
    }
  };

  const apagarConversa = async () => {
    setApagandoConversa(false);
    try {
      await apagarConversaParaMim(request, detalhe.id);
      setConversas((atuais) => atuais.filter((c) => c.id !== detalhe.id));
      toast.success("Conversa apagada para você.");
      irParaLista();
    } catch {
      /* o request já avisou */
    }
  };

  // Direta com um PIN da fábrica sem vínculo: a tela confere se é a mesma
  // pessoa de um login do ERP (a lista de pessoas traz a sugestão).
  const diretaComOperador = detalhe?.tipo === "direta"
    && (detalhe.participantes || []).some((p) => p.tipo === "operador" && p.id !== detalhe.eu?.id);
  useEffect(() => {
    if (!diretaComOperador || pessoasDaUnidade) return;
    pessoas(request).then(setPessoasDaUnidade).catch(() => setPessoasDaUnidade({ usuarios: [], operadores: [] }));
  }, [diretaComOperador, pessoasDaUnidade, request]);
  const mesmaPessoa = mesmaPessoaDaDireta(detalhe, pessoasDaUnidade);
  const vincularMesmaPessoa = async () => {
    try {
      // O servidor junta as duas diretas ao salvar o vínculo (ADR-0077).
      await atualizarOperador(request, mesmaPessoa.operador.id, { usuario: mesmaPessoa.usuario.id });
      toast.success(`Pronto: ${mesmaPessoa.usuario.nome} agora é uma pessoa só no chat.`);
      setPessoasDaUnidade(null);
      recarregarLista();
      irParaLista();
    } catch {
      /* o request já avisou */
    }
  };

  const janelaMs = (config?.janela_edicao_min ?? 15) * 60000;
  const agora = Date.now();
  // O MESMO objeto entre um desenho e outro: com um novo a cada vez, o `memo`
  // do balão não valia e todo balão redesenhava a cada "digitando".
  const acoes = useMemo(() => ({
    responder: (m) => { setEditando(null); setRespostaA(m); },
    reagir: (m, emoji) => reagir(request, m.id, emoji).then(() => { if (!conectadoRef.current) recarregarConversa(m.conversa_id); }).catch(() => undefined),
    editar: (m) => { setRespostaA(null); setEditando(m); },
    apagar: (m) => setApagando(m),
    abrir: (anexos, indice) => setVisualizando({ anexos, indice }),
    tentarDeNovo: (m) => { atualizarPendente(m.id_do_cliente, { pendente: "enviando" }); despachar(m.id_do_cliente); },
    descartar: (m) => { pendentesRef.current.delete(m.id_do_cliente); setMensagens((atuais) => atuais.filter((x) => x.id !== m.id)); },
    copiar: (m) => copiarTexto(m.texto).then((ok) => (ok ? toast.success("Texto copiado.") : toast.error("Não deu para copiar."))),
    encaminhar: (m) => setEncaminhando(m),
    // Relê o detalhe: o banner das fixadas vem dele (ação rara, uma leitura).
    fixar: (m) => fixarMensagem(request, m.id, !m.fixada).then(() => recarregarConversa(m.conversa_id)).catch(() => undefined),
    virarTarefa: (m) => setTarefaDe(m),
    // Quem não leu recebe o aviso de novo; o servidor diz quem foi (e recusa repetir em 5 min).
    notificarDeNovo: (m) => notificarDeNovo(request, m.id).then((r) => toast.success(textoDoLembrete(r?.avisados))).catch(() => undefined),
    // Tocou o áudio de outra pessoa: azul aqui na hora; o servidor avisa quem mandou.
    ouvirAudio: (m, anexo) => {
      setMensagens((atuais) => marcarOuvidoNaMensagem(atuais, m.id, anexo.id));
      marcarAudioOuvido(request, anexo.id).catch(() => undefined);
    },
  }), [request, recarregarConversa, atualizarPendente, despachar, toast]);

  const grupos = useMemo(() => agruparConversas(conversas, busca), [conversas, busca]);
  const porDia = useMemo(() => agruparPorDia(mensagens), [mensagens]);
  const ativos = (detalhe?.participantes || []).filter((p) => !p.saiu_em);
  const fotoPorParticipante = useMemo(
    () => Object.fromEntries((detalhe?.participantes || []).filter((p) => p.foto_url).map((p) => [p.id, p.foto_url])),
    [detalhe],
  );
  // Canal geral: ler é de todos da unidade, escrever exige
  // chat_canal_geral:postar (o backend recusa sem ela). Sem a permissão o
  // campo some em vez de deixar digitar e falhar no envio.
  const somenteLeituraGeral =
    detalhe?.tipo === "geral" && !hasPermission("chat_canal_geral:postar");
  const podeGerenciar = !!detalhe && detalhe.tipo === "grupo" && (podeGrupos || detalhe.eu?.administrador);
  const anexosDaConversa = mensagens.filter((m) => !m.apagada).flatMap((m) => m.anexos || []);
  const jaEstao = new Set(ativos.flatMap((p) => [p.usuario_id, p.operador_id]).filter(Boolean));
  const tituloDaConversa = detalhe ? (detalhe.tipo === "lote" && detalhe.batch_code ? `Lote ${detalhe.batch_code}` : detalhe.nome) : "";
  const quemDigita = (conversaId && digitando[conversaId]) || [];
  const subtitulo = quemDigita.length === 1 ? `${quemDigita[0]} está digitando…` : quemDigita.length > 1 ? `${quemDigita.length} pessoas digitando…`
    : detalhe?.tipo === "direta" ? "Conversa direta" : detalhe?.tipo === "lote" ? `Conversa do lote${detalhe.arquivada_em ? " · arquivada" : ""} · ${ativos.length} pessoas`
    : detalhe?.tipo === "geral" ? `Canal geral da unidade · ${ativos.length} pessoas` : `${ativos.length} pessoas`;

  const painel = detalhe && (
    <PainelDaConversa
      detalhe={detalhe} titulo={tituloDaConversa} telaLarga={telaLarga} aberto={painelAberto}
      aoFechar={() => setPainelAberto(false)} request={request} podeGerenciar={podeGerenciar} anexos={anexosDaConversa}
      aoMudar={() => { recarregarConversa(detalhe.id); recarregarLista(); }}
      aoRenomear={() => setRenomeando(detalhe.nome)} aoAdicionar={() => setAdicionando(true)}
      aoVer={(anexos, indice) => setVisualizando({ anexos, indice })} aoSair={() => setSaindo(true)}
      aoApagar={() => setApagandoConversa(true)}
    />
  );

  return (
    <div ref={raizRef} className={`pchat-pagina${naLateral ? " na-lateral" : ""}`}>
    <div className={`pchat ${naLateral ? "na-lateral" : ""} ${estreita ? (conversaId ? "mostra-conversa" : "mostra-lista") : ""}`}>
      <aside className="pchat-lista" aria-label="Conversas">
        <div className="topo">
          <h2>
            Chat
            <span className="pchat-topo-acoes">
              <button type="button" className="pchat-btn" title="Assistente (só consulta)" aria-label="Abrir o assistente" onClick={() => setAssistenteAberto(true)}><Ic d={ICONE.faisca} size={16} /></button>
              <button type="button" className="pchat-btn" title="Configurações do chat" aria-label="Configurações do chat" onClick={() => setConfigAberta(true)}><Ic d={ICONE.engrenagem} size={16} /></button>
              {naLateral ? botoesDaLateral : (
                <button type="button" className="pchat-btn" title="Abrir na lateral e seguir usando as outras telas" aria-label="Abrir o chat na lateral" onClick={levarParaALateral}><Ic d={ICONE.lateral} size={16} /></button>
              )}
              {podeEnviar && <button type="button" className="pchat-btn texto" onClick={() => setNova(true)}><Ic d={ICONE.plus} size={14} /> Nova</button>}
            </span>
          </h2>
          <input className="pchat-busca" placeholder="Buscar conversa, contato ou lote" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar conversa ou contato" />
        </div>
        <div className="rolagem">
          {carregandoLista && <div className="pchat-vazio">Carregando…</div>}
          {!carregandoLista && grupos.length === 0 && contatos.length === 0 && <div className="pchat-vazio">{busca ? "Nada com esse nome." : "Nenhuma conversa ainda."}</div>}
          {contatos.length > 0 && (
            <div>
              <div className="pchat-grupo-rotulo">Contatos</div>
              {contatos.map((p) => (
                <button key={`${p.tipo}-${p.id}`} type="button" className="pchat-conv" onClick={() => abrirDiretaCom(p)} title={`Começar conversa com ${p.nome}`}>
                  <Avatar nome={p.nome} foto={p.foto_url} />
                  <span className="txt">
                    <span className="nome"><span>{p.nome}</span></span>
                    <span className="previa">{p.detalhe ? `${p.detalhe} · ` : ""}começar conversa</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {achados && achados.length > 0 && (
            <div>
              <div className="pchat-grupo-rotulo">Mensagens com "{busca.trim()}"</div>
              {achados.map((m) => (
                <button key={m.id} type="button" className="pchat-conv" onClick={() => { setBusca(""); irPara(m.conversa_id, { mensagem: m.id }); }}>
                  <span className="txt">
                    <span className="nome"><span>{m.conversa_tipo === "lote" ? `Lote · ${m.conversa_nome}` : m.conversa_nome}</span><small>{horaCurta(m.criada_em)}</small></span>
                    <span className="previa"><em>{m.autor_nome}:</em> {m.texto || (m.anexos?.[0]?.nome || m.cartao?.ref || "")}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {grupos.map((g) => (
            <div key={g.rotulo}>
              <div className="pchat-grupo-rotulo">{g.rotulo}</div>
              {g.conversas.map((c) => {
                const nome = c.tipo === "lote" && c.batch_code ? `Lote ${c.batch_code}` : c.nome;
                const dig = digitando[c.id] || [];
                return (
                  <button key={c.id} type="button" className={`pchat-conv ${c.id === conversaId ? "ativa" : ""}`} onClick={() => irPara(c.id)}>
                    <Avatar nome={nome} bot={c.tipo === "geral"} foto={c.foto_url} />
                    <span className="txt">
                      <span className="nome"><span>{c.fixada && <span aria-label="fixada" title="fixada">📌 </span>}{nome}{c.silenciada && <span title="silenciada" aria-label="silenciada"> 🔕</span>}</span><small>{quandoCurto(c.ultima_mensagem_em)}</small></span>
                      <span className="previa">
                        {dig.length > 0
                          ? <Digitando texto={c.tipo === "direta" ? "digitando" : `${dig[0]} está digitando`} />
                          : c.ultima ? <PreviaDaUltima ultima={c.ultima} direta={c.tipo === "direta"} /> : <span style={{ color: "var(--ink-4)" }}>{c.membros} pessoas</span>}
                      </span>
                    </span>
                    {c.nao_lidas > 0 && <span className={`pchat-nao-lidas ${c.silenciada ? "silenciada" : ""}`}>{c.nao_lidas > 99 ? "99+" : c.nao_lidas}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      <section className="pchat-conversa" aria-label={detalhe ? `Conversa ${detalhe.nome}` : "Conversa"}>
        {!conversaId && (
          <div className="pchat-vazio">
            <div>
              <Ic d={ICONE.message} size={40} />
              <div style={{ marginTop: 10, fontWeight: 600 }}>{estadoDoStream === "conectado" ? "Escolha uma conversa" : "Reconectando ao chat…"}</div>
              <div style={{ marginTop: 4, fontSize: 12.5 }}>Os lotes em corte aparecem primeiro. A conversa de cada lote nasce quando ele é aberto no PharuCut.</div>
            </div>
          </div>
        )}
        {conversaId && erroDaConversa && <div className="pchat-vazio">Conversa não encontrada: ela pode ter sido arquivada, ou você não faz parte dela.</div>}
        {conversaId && !erroDaConversa && !detalhe && <div className="pchat-vazio">Abrindo a conversa…</div>}
        {detalhe && !erroDaConversa && detalhe.id === conversaId && (
          <>
            <header className="pchat-cab">
              {estreita && <button type="button" className="pchat-btn" aria-label="Voltar para a lista" onClick={irParaLista}><Ic d={ICONE.back} size={18} /></button>}
              <button type="button" className="pchat-cab-nome" title="Ver perfil, arquivos e pessoas" aria-label={`Ver detalhes de ${tituloDaConversa}`} onClick={() => setPainelAberto((v) => !v)}>
                <Avatar nome={detalhe.nome} bot={detalhe.tipo === "geral"} foto={detalhe.foto_url} />
                <span className="titulo"><b>{tituloDaConversa}</b><span>{subtitulo}</span></span>
              </button>
              {naLateral && <span className="pchat-topo-acoes">{botoesDaLateral}</span>}
            </header>
            {mesmaPessoa && (
              <div className="pchat-aviso-pessoa" role="note">
                <Ic d={ICONE.info} size={15} />
                <span><b>{mesmaPessoa.operador.nome}</b> entra pelo PIN da fábrica e parece ser <b>{mesmaPessoa.usuario.nome}</b>, que tem login no ERP. Vinculando, as duas conversas viram uma só.</span>
                {hasPermission("operador:manage")
                  ? <button type="button" className="pchat-btn texto" onClick={vincularMesmaPessoa}>Vincular</button>
                  : <small>Quem cuida da Mão de obra pode vincular.</small>}
              </div>
            )}
            {detalhe.fixadas?.length > 0 && (
              <div className="pchat-fixadas" aria-label="Mensagens fixadas">
                {detalhe.fixadas.map((f) => (
                  <button key={f.id} type="button" onClick={() => irPara(conversaId, { mensagem: f.id, substituir: true })}>
                    <Ic d={ICONE.pino} size={13} /><b>{f.autor_nome}:</b> <span>{f.texto || "anexo"}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="pchat-fluxo" ref={fluxoRef}>
              {temMais && <button type="button" className="pchat-btn texto" style={{ alignSelf: "center" }} onClick={carregarAnteriores}>Carregar mensagens anteriores</button>}
              {mensagens.length === 0 && <div className="pchat-vazio">Ninguém falou aqui ainda.</div>}
              {porDia.map((g) => (
                <React.Fragment key={g.dia}>
                  <div className="pchat-dia">{g.rotulo}</div>
                  {g.mensagens.map((m, i) => {
                    const anterior = g.mensagens[i - 1];
                    const mesmoAutor = anterior && anterior.autor_id === m.autor_id && anterior.autor_tipo === m.autor_tipo && !anterior.apagada;
                    return <Balao key={m.id} m={m} podeEditar={agora - new Date(m.criada_em).getTime() < janelaMs} acoes={acoes} mostrarAutor={!mesmoAutor} request={request} versao={versaoDosCartoes} destacada={destacada === m.id} fotoDoAutor={fotoPorParticipante[m.autor_id] || null} />;
                  })}
                </React.Fragment>
              ))}
            </div>
            {agendadas.length > 0 && (
              <div className="pchat-agendadas" aria-label="Mensagens agendadas por você">
                {agendadas.map((a) => (
                  <span key={a.id} className={`pchat-chip${a.falha ? " falhou" : ""}`} title={a.falha ? `Não saiu: ${a.falha}` : a.texto}>
                    <Ic d={ICONE.relogio} size={12} />
                    <span>{a.falha ? "Não saiu" : new Date(a.enviar_em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })} · {a.texto}</span>
                    <button type="button" aria-label={a.falha ? "Dispensar o aviso" : "Cancelar o agendamento"} onClick={() => cancelarAgendada(request, a.id).then(() => recarregarAgendadas(conversaId)).catch(() => undefined)}><Ic d={ICONE.x} size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="pchat-digitando">{quemDigita.length > 0 && <Digitando texto={quemDigita.length === 1 ? `${quemDigita[0]} está digitando` : `${quemDigita.length} pessoas digitando`} />}</div>
            {somenteLeituraGeral ? (
              <div className="pchat-rodape-legal">Só leitura neste canal.</div>
            ) : (
            <Escrita
              key={detalhe.id}
              aoEnviar={aoEnviar}
              aoDigitar={() => avisarDigitando(request, conversaId).catch(() => undefined)}
              respostaA={respostaA}
              aoCancelarResposta={() => setRespostaA(null)}
              editando={editando}
              aoCancelarEdicao={() => setEditando(null)}
              desabilitada={!podeEnviar || !!detalhe.arquivada_em}
              limiteMb={config?.limite_arquivo_mb ?? 50}
              nomes={ativos.filter((p) => p.id !== detalhe.eu?.id).map((p) => p.nome)}
              pode={hasPermission}
              aoEscolherTom={(tom) => { aplicarTomDePele(tom); salvarPreferenciasDoChat(request, { tom_de_pele: tom }).catch(() => undefined); }}
              aoAgendar={aoAgendar}
            />
            )}
            {!somenteLeituraGeral && <div className="pchat-rodape-legal">{detalhe.arquivada_em ? "Conversa arquivada: só leitura." : `Editar ou apagar vale por ${config?.janela_edicao_min ?? 15} min. O histórico fica guardado.`}</div>}
          </>
        )}
      </section>

      {/* Só aparece ao clicar no nome da conversa (ordem da dona, 2026-09-29). */}
      {painelAberto && painel}

      <Modal open={!!apagando} titulo="Apagar a mensagem?" aoFechar={() => setApagando(null)}>
        <p style={{ margin: 0, color: "var(--ink-3)" }}>Quem está na conversa vê "Mensagem apagada". O texto continua no histórico para quem pode auditar.</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="pchat-btn texto" onClick={() => setApagando(null)}>Manter</button>
          <button type="button" className="pchat-btn texto" style={{ background: "var(--danger-solid)", borderColor: "var(--danger-solid)", color: "#fff" }} onClick={() => { const alvo = apagando; setApagando(null); apagarMensagem(request, alvo.id).then(() => { if (!conectadoRef.current) { recarregarConversa(conversaId); recarregarLista(); } }).catch(() => undefined); }}>Apagar</button>
        </div>
      </Modal>

      <Modal open={nova} titulo="Nova conversa" aoFechar={() => setNova(false)}>
        {podeGrupos && (
          <div className="pchat-seg">
            <button type="button" className={modoNova === "direta" ? "ativo" : ""} onClick={() => setModoNova("direta")}>Direta</button>
            <button type="button" className={modoNova === "grupo" ? "ativo" : ""} onClick={() => setModoNova("grupo")}>Grupo</button>
          </div>
        )}
        {modoNova === "grupo" && podeGrupos && <input className="pchat-busca" placeholder="Nome do grupo" value={nomeDoGrupo} onChange={(e) => setNomeDoGrupo(e.target.value)} aria-label="Nome do grupo" />}
        {nova && (modoNova === "direta" || !podeGrupos ? (
          <EscolhaDePessoas key="direta" request={request} multiplas={false} rotulo="Abrir" aoConfirmar={([p]) => criarDireta(request, { usuarioId: p.tipo === "usuario" ? p.id : undefined, operadorId: p.tipo === "operador" ? p.id : undefined }).then((d) => { setNova(false); recarregarLista(); irPara(d.id); }).catch(() => undefined)} />
        ) : (
          <EscolhaDePessoas key="grupo" request={request} multiplas rotulo="Criar grupo" aoConfirmar={(ps) => {
            const limpo = nomeDoGrupo.trim();
            if (!limpo) { toast.error("Dê um nome ao grupo."); return; }
            criarGrupo(request, { nome: limpo, usuarios: ps.filter((p) => p.tipo === "usuario").map((p) => p.id), operadores: ps.filter((p) => p.tipo === "operador").map((p) => p.id) })
              .then((d) => { setNova(false); setNomeDoGrupo(""); recarregarLista(); irPara(d.id); }).catch(() => undefined);
          }} />
        ))}
      </Modal>

      <Modal open={adicionando} titulo="Adicionar ao grupo" aoFechar={() => setAdicionando(false)}>
        {adicionando && <EscolhaDePessoas request={request} excluir={jaEstao} multiplas rotulo="Adicionar" aoConfirmar={async (ps) => {
          setAdicionando(false);
          for (const p of ps) {
            try { await adicionarParticipante(request, detalhe.id, { usuarioId: p.tipo === "usuario" ? p.id : undefined, operadorId: p.tipo === "operador" ? p.id : undefined }); } catch { /* o request já avisou */ }
          }
          recarregarConversa(detalhe.id);
        }} />}
      </Modal>

      <Modal open={renomeando !== null} titulo="Renomear o grupo" aoFechar={() => setRenomeando(null)}>
        <input className="pchat-busca" value={renomeando || ""} onChange={(e) => setRenomeando(e.target.value)} aria-label="Nome do grupo" autoFocus />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="pchat-btn texto" onClick={() => setRenomeando(null)}>Cancelar</button>
          <button type="button" className="pchat-btn enviar texto" disabled={!renomeando?.trim()} onClick={() => { const nome = renomeando.trim(); setRenomeando(null); alterarConversa(request, detalhe.id, { nome }).then(() => { recarregarConversa(detalhe.id); recarregarLista(); }).catch(() => undefined); }}>Salvar</button>
        </div>
      </Modal>

      <Modal open={saindo} titulo="Sair do grupo?" aoFechar={() => setSaindo(false)}>
        <p style={{ margin: 0, color: "var(--ink-3)" }}>Você deixa de receber as mensagens deste grupo. Para voltar, alguém que administra precisa te adicionar.</p>
        <div className="pchat-modal-acoes">
          <button type="button" className="pchat-btn texto" onClick={() => setSaindo(false)}>Ficar</button>
          <button type="button" className="pchat-btn texto perigo" onClick={sairDoGrupo}>Sair do grupo</button>
        </div>
      </Modal>

      <Modal open={apagandoConversa} titulo="Apagar esta conversa?" aoFechar={() => setApagandoConversa(false)}>
        <p style={{ margin: 0, color: "var(--ink-3)" }}>
          Ela some só para você: {detalhe?.tipo === "direta" ? `${detalhe.nome} continua` : "as outras pessoas continuam"} vendo tudo.
          Se chegar mensagem nova, a conversa volta para a sua lista, só com o que vier depois.
        </p>
        <div className="pchat-modal-acoes">
          <button type="button" className="pchat-btn texto" onClick={() => setApagandoConversa(false)}>Cancelar</button>
          <button type="button" className="pchat-btn texto perigo" onClick={apagarConversa}>Apagar para mim</button>
        </div>
      </Modal>

      {agendando && <AgendarModal texto={agendando.texto} aoFechar={fecharAgendamento} aoConfirmar={confirmarAgendamento} />}
      {encaminhando && <EncaminharModal mensagem={encaminhando} conversas={conversas.filter((c) => c.id !== encaminhando.conversa_id)} aoFechar={() => setEncaminhando(null)} aoConfirmar={confirmarEncaminhar} />}
      {tarefaDe && detalhe && <TarefaModal mensagem={tarefaDe} pessoas={ativos} eu={detalhe.eu} aoFechar={() => setTarefaDe(null)} aoConfirmar={confirmarTarefa} />}
      {configAberta && <ConfiguracoesDoChat request={request} config={config} toast={toast} aoSalvar={setConfig} aoFechar={() => setConfigAberta(false)} />}
      {assistenteAberto && <Assistente request={request} aoFechar={() => setAssistenteAberto(false)} />}

      {visualizando && <Visualizador anexos={visualizando.anexos} indice={visualizando.indice} aoFechar={() => setVisualizando(null)} />}
    </div>
    </div>
  );
}
